/**
 * What the home-screen widget says (mobile M7), built from the dashboard the
 * app already fetched — the widget never computes or guesses a figure.
 *
 * "Due by month end" stays two figures, never one sum (CLAUDE.md, Design):
 * the printed bills (gold in the widget) and the spend still building from
 * alerts (blue, "+ … building"). With "hide amounts" on (the owner's answer to
 * Q7: show amounts, with a switch to hide them), every amount becomes dots.
 */

import type { DashboardSummary } from "@/lib/api/client";
import { dayParts, formatMoney } from "@/lib/format";
import type { MonthEndWidget } from "@/platform/widget";

const HIDE_KEY = "mydeck.widget.hide-amounts";
const DOTS = "₹ ••••";

export function widgetHidesAmounts(): boolean {
  try {
    return localStorage.getItem(HIDE_KEY) === "1";
  } catch {
    return false;
  }
}

export function setWidgetHidesAmounts(hide: boolean): void {
  try {
    if (hide) localStorage.setItem(HIDE_KEY, "1");
    else localStorage.removeItem(HIDE_KEY);
  } catch {
    /* keeps the previous choice */
  }
}

function clock(ms: number): string {
  const d = new Date(ms);
  const p = (n: number) => String(n).padStart(2, "0");
  return `${p(d.getHours())}:${p(d.getMinutes())}`;
}

export function widgetSummary(d: DashboardSummary, asOf: number, hide: boolean): MonthEndWidget {
  const m = d.due_by_month_end;
  const end = dayParts(m.month_end);
  const money = (v: string) => (hide ? DOTS : formatMoney(v));
  const building = Number(m.building_total) > 0;
  const next = d.runway
    .filter((e) => e.urgency !== "paid" && e.urgency !== "unconfirmed" && e.due_date >= d.today)
    .sort((a, b) => a.due_date.localeCompare(b.due_date))[0];
  const nextDay = next ? dayParts(next.due_date) : null;
  return {
    month: m.month_end.slice(0, 7),
    label: `Due by ${end.day} ${end.month}`,
    printed: money(m.printed_total),
    note:
      m.printed_count === 0
        ? "No printed bill due this month"
        : `${m.printed_count} printed bill${m.printed_count === 1 ? "" : "s"} to pay`,
    building: building ? `+ ${money(m.building_total)} building, from alerts` : "",
    next: next && nextDay ? `Next: ${next.nickname ?? `•••• ${next.last4}`} · ${nextDay.day} ${nextDay.month}` : "",
    asOf: `as of ${clock(asOf)}`,
  };
}
